import {
  type ComputedRef,
  inject,
  type InjectionKey,
  type Ref,
  ref,
  unref,
  getCurrentInstance,
  onBeforeUpdate,
  computed,
  reactive,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  type InputHTMLAttributes,
} from 'vue';
import type { PfFormSelect, PfTextInput } from '@vue-patternfly/core';
import { useFormGroupFieldId } from '@vue-patternfly/core/components/Form/common';
import type { ComponentExposed } from 'vue-component-type-helpers';
import { useManagedProp } from '@common/use';
import { DateTime } from 'luxon';
import ip from 'ip-regex';
import { parseDateTime } from './date';

/** Tipo per elementi form nativi HTML che supportano validazione */
export type NativeValidityFormElement = HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement;
/** Tipo per elementi form (nativi o PatternFly) con API di validazione */
export type ValidityFormElement = NativeValidityFormElement | ComponentExposed<typeof PfTextInput> | ComponentExposed<typeof PfFormSelect>;

/**
 * Composable per gestire la validazione di un elemento input con override di checkValidity/reportValidity
 * @param input - Ref all'elemento form da validare
 * @param doValidate - Funzione opzionale di validazione custom da chiamare prima dei check nativi
 * @returns Oggetto con stato di validità e funzioni per check/report/set validity
 */
export function useInputValidity(input: Ref<ValidityFormElement | null>, doValidate?: () => void) {
  const isInvalid = ref(false);
  const el = computed(() => {
    if (input.value instanceof HTMLTextAreaElement || input.value instanceof HTMLInputElement || input.value instanceof HTMLSelectElement) {
      return input.value;
    }
    return (input.value?.input ?? (input.value as any)?.$el) as NativeValidityFormElement | null;
  });

  const refreshValidity = () => {
    isInvalid.value = false;
    nextTick(checkValidity);
  };

  const checkValidity = () => {
    doValidate?.();
    isInvalid.value = el.value ? !(el.value.constructor.prototype as NativeValidityFormElement).checkValidity.call(el.value) : true;
    return !isInvalid.value;
  };

  const reportValidity = () => {
    doValidate?.();
    isInvalid.value = el.value ? !(el.value.constructor.prototype as NativeValidityFormElement).reportValidity.call(el.value) : true;
    return !isInvalid.value;
  };

  const setCustomValidity = (validity: string) => {
    el.value?.setCustomValidity(validity);
  };

  onMounted(() => {
    if (el.value) {
      el.value.checkValidity = checkValidity;
      el.value.reportValidity = reportValidity;
    }
  });

  onBeforeUnmount(() => {
    if (el.value) {
      el.value.checkValidity = (el.value.constructor.prototype as NativeValidityFormElement).checkValidity;
      el.value.reportValidity = (el.value.constructor.prototype as NativeValidityFormElement).reportValidity;
    }
  });

  return {
    /** Stato reattivo che indica se l'input è invalido */
    isInvalid,
    /** Resetta lo stato di validità e pianifica un nuovo check al prossimo tick */
    refreshValidity,
    /** Verifica la validità dell'input senza mostrare messaggi all'utente */
    checkValidity,
    /** Verifica la validità dell'input e mostra messaggi di errore all'utente */
    reportValidity,
    /** Imposta un messaggio di validità custom sull'elemento */
    setCustomValidity,
  };
}

/** Injection key per fornire stato disabled a livello di form group (propagato ai figli) */
export const FormGroupDisabledInjectionKey = Symbol('FormGroupDisabledInjectionKey') as InjectionKey<boolean | ComputedRef<boolean>>;

/**
 * Registra il campo nel form group che lo contiene, come fanno gli input di PatternFly: l'etichetta del gruppo è
 * collegata al primo campo, che riceve l'id del gruppo se non ne ha uno. Gli altri campi, e quelli fuori da un form
 * group, senza aria-label né aria-labelledby hanno come nome accessibile il placeholder, il title o il name.
 */
export function useFormGroupField(fieldAttrs: () => Record<string, unknown>) {
  const { id, linked } = useFormGroupFieldId(() => {
    const attrs = fieldAttrs();
    return typeof attrs.id === 'string' ? attrs.id : undefined;
  });

  const ariaLabel = computed(() => {
    const attrs = fieldAttrs();
    if (linked.value || attrs['aria-label'] || attrs['aria-labelledby']) {
      return undefined;
    }
    for (const label of [attrs.placeholder, attrs.title, attrs.name]) {
      if (typeof label === 'string' && label !== '') {
        return label;
      }
    }
    return undefined;
  });

  return { id, ariaLabel };
}

interface InputTypeValidator {
  title: string;
  test?: (value: string | number | unknown) => boolean | string;
  pattern?: string;
}

type I18nT = (s: string, named?: object) => string;

const defineCustomTypes: ($t: I18nT) => Record<string, InputTypeValidator> = ($t: I18nT) => ({
  ip: {
    title: 'Indirizzo IP non valido',
    test: (v: string | number | unknown) => typeof v === 'string' && ip({ exact: true }).test(v),
  },

  ipv4: {
    title: 'Indirizzo IPv4 non valido',
    test: (v: string | number | unknown) => typeof v === 'string' && ip.v4({ exact: true }).test(v),
  },

  ipv6: {
    title: 'Indirizzo IPv6 non valido',
    test: (v: string | number | unknown) => typeof v === 'string' && ip.v6({ exact: true }).test(v),
  },

  hostname: {
    title: $t(
      'Non è un hostname valido. Sono permesse solo lettere (a-z), numeri (0-9) e il trattino (segno meno). I numeri non sono permessi come primo carattere e il trattino non è permesso come primo o ultimo carattere.',
    ),
    pattern: '^[a-z]+[a-z0-9\\-]*[a-z0-9]+$',
  },

  identifier: {
    title: $t(
      'Non è un identificatore valido. Sono permesse solo lettere (a-z), numeri (0-9) e il trattino basso (underscore). I numeri non sono permessi come primo carattere.',
    ),
    pattern: '^[a-z_]+[a-z0-9_]*$',
  },

  domain: {
    title: $t('Non è un nome di dominio internet valido'),
    pattern: '^([a-zàâäèéêëìîïòôöùûüæœçÿβñ0-9]([\\-a-zàâäèéêëìîïòôöùûüæœçÿβñ0-9]*[a-zàâäèéêëìîïòôöùûüæœçÿβñ0-9])?\\.)+[a-zàâäèéêëìîïòôöùûüæœçÿβñ0-9]+$',
  },

  dkim: {
    title: $t('Formato DKIM non valido'),
    test: (value: string | number | unknown) => {
      if (typeof value !== 'string') {
        return false;
      }
      const parts = value.split(';').filter(Boolean);
      const data: Record<string, string> = {};
      for (const part of parts) {
        const kv = part.trim().split('=');
        if (kv.length != 2 || typeof kv[0] !== 'string' || typeof kv[1] !== 'string') {
          return false;
        }
        data[kv[0] as string] = kv[1] as string;
      }
      if (data.v && data.v != 'DKIM1') {
        return $t("Formato DKIM non valido: v=DKIM1 è l'unico valore permesso");
      }
      if (data.k && data.k != 'rsa') {
        return $t("Formato DKIM non valido: k=rsa è l'unico valore permesso");
      }
      if (!data.p) {
        return $t('Formato DKIM non valido: "p=" mancante');
      }
      try {
        atob(data.p);
      } catch {
        return $t('Formato DKIM non valido: "p=" non è in codifica base64');
      }
      return true;
    },
  },

  dmarc: {
    title: $t('Formato DMARC non valido'),
    test: (value: string | number | unknown) => {
      if (typeof value !== 'string') {
        return false;
      }
      const parts = value
        .split(';')
        .map(s => s.trim())
        .filter(Boolean);
      const data: Record<string, string> = {};
      for (const part of parts) {
        const kv = part.trim().split('=');
        if (kv.length != 2 || typeof kv[0] !== 'string' || typeof kv[1] !== 'string') {
          return false;
        }
        data[kv[0] as string] = kv[1] as string;
      }
      if (data.v && data.v != 'DMARC1') {
        return $t("Formato DMARC non valido: v=DMARC1 è l'unico valore permesso");
      }
      if (!data.p) {
        return $t('Formato DMARC non valido: "p=" mancante');
      }
      if (data.pct) {
        const pct = Number(data.pct);
        if (isNaN(pct)) {
          return $t('Formato DMARC non valido: pct deve essere un numero');
        }
        if (pct < 1 || pct > 100) {
          return $t('Formato DMARC non valido: pct deve essere un numero compreso tra 1 e 100');
        }
      }
      if (data.fo && !data.fo.split(':').every(fo => ['0', '1', 'd', 's'].includes(fo))) {
        return $t(`Formato DMARC non valido: il campo fo può contenere solo valori "0", "1", "d" o "s" separati da ":"`);
      }
      for (const field of ['rua', 'ruf']) {
        if (data[field]) {
          const emails = data[field].split(',');
          for (const email of emails) {
            if (!email.startsWith('mailto:')) {
              return $t(`Formato DMARC non valido: le email in {field} devono cominciare con mailto:`, { field });
            }
          }
        }
      }
      for (const field of ['p', 'sp']) {
        if (data[field] && !['none', 'quarantine', 'reject'].includes(data[field])) {
          return $t(`Formato DMARC non valido: il campo {field} può contenere solo "none", "quarantine" o "reject"`, { field });
        }
      }
      for (const field of ['adkim', 'aspf']) {
        if (data[field] && !['s', 'r'].includes(data[field])) {
          return $t(`Formato DMARC non valido: il campo {field} può contenere solo "s" o "r"`, { field });
        }
      }
      return true;
    },
  },
});
/**
 * Tipi di input supportati: tipi HTML5 nativi + custom (ip, ipv4, ipv6, hostname, identifier, domain, dkim, dmarc)
 */
export type InputType =
  | keyof ReturnType<typeof defineCustomTypes>
  | 'text'
  | 'number'
  | 'email'
  | 'tel'
  | 'hidden'
  | 'color'
  | 'date'
  | 'time'
  | 'datetime-local'
  | 'file'
  | 'month'
  | 'password'
  | 'range'
  | 'search'
  | 'url'
  | 'new-password'
  | 'datetime';

/**
 * Props per componenti form element con gestione validazione e v-model avanzato
 * @template T - Tipo di input (InputType)
 * @template N - Flag per indicare se il v-model.number è attivo
 */
export interface XFormElementProps<T extends InputType = 'text', N extends boolean = false> {
  modelValue?: string | number | null;
  modelModifiers?: {
    number?: N;
    trim?: boolean;
    lazy?: boolean;
  };
  prepend?: string;
  append?: string;
  disabled?: boolean;
  withCheckbox?: string | boolean;
  checked?: boolean;
  resetValue?: string | number;
  type?: T;
  pattern?: string;
  title?: string;
  customValidity?: string;
  step?: string | number;
  autoRound?: boolean;
  class?: InputHTMLAttributes['class'];
}

/**
 * Eventi emessi dai componenti form element (update:checked, update:modelValue)
 * @template T - Tipo di input (InputType)
 * @template N - Flag per indicare se il v-model.number è attivo
 */
export interface XFormElementEmits<T extends InputType = 'text', N extends boolean = false> {
  (name: 'update:checked', value: boolean): void;
  (name: 'update:modelValue', value: N extends true ? number : T extends 'number' ? number : string): void;
}

/**
 * Composable principale per implementare componenti form element con validazione custom types e gestione avanzata
 * @template T - Tipo di input (InputType)
 * @template N - Flag per indicare se il v-model.number è attivo
 * @param props - Props del componente form element
 * @param emit - Funzione emit per eventi
 * @param $t - Funzione i18n per traduzioni (default: identity function)
 * @returns Oggetto con stato reattivo, handlers e metodi per gestire input e validazione
 */
export function useXFormElement<T extends InputType, N extends boolean>(
  props: Readonly<XFormElementProps<T, N>>,
  emit: XFormElementEmits<T, N>,
  $t: I18nT = s => s,
) {
  const input: Ref<ValidityFormElement | null> = ref(null);
  const value = useManagedProp<(N extends true ? number : T extends 'number' ? number : string) | null, string | number | null>(
    'modelValue',
    '',
    (v): N extends true ? number : T extends 'number' ? number : string => {
      if (props.modelModifiers?.number || props.type === 'number') {
        return Number(v) as any;
      }
      return String(v) as any;
    },
  );
  const instance = getCurrentInstance()?.proxy;
  const internalChecked = ref(false);
  const withSlot: Record<string, boolean> = reactive({});
  const customTypes = defineCustomTypes($t);

  const groupDisabled = inject(FormGroupDisabledInjectionKey, false);

  const hasPreAddon = computed(() => {
    return props.prepend || withSlot.prepend || props.withCheckbox;
  });

  const hasPostAddon = computed(() => {
    return props.append || withSlot.append;
  });

  const effectiveDisabled = computed(() => {
    return props.disabled || unref(groupDisabled) || (Boolean(props.withCheckbox) && !effectiveChecked.value);
  });

  const effectiveChecked = computed({
    get: () => props.checked ?? internalChecked.value ?? false,
    set(value: boolean) {
      value = Boolean(value);
      internalChecked.value = value;
      emit('update:checked', value);
    },
  });

  const sourceDate = computed(() => {
    if (inputType.value === 'datetime-local' && value.value) {
      return parseDateTime(value.value);
    }
    return null;
  });

  const inputType = computed(() => {
    if (props.type === 'new-password') {
      return 'password';
    }
    if (props.type === 'datetime') {
      return 'datetime-local';
    }
    const nativeInputTypes = [
      'text',
      'number',
      'email',
      'tel',
      'hidden',
      'color',
      'date',
      'time',
      'datetime-local',
      'file',
      'month',
      'password',
      'range',
      'search',
      'url',
    ] as const;
    if (props.type && (nativeInputTypes as readonly string[]).includes(props.type)) {
      return props.type as (typeof nativeInputTypes)[number];
    }
    return 'text';
  });

  const inputValue = computed(() => {
    if (inputType.value === 'datetime-local' && sourceDate.value !== null) {
      const value = sourceDate.value;
      // Remove timezone and seconds/milliseconds from string
      return value.toISO()?.replace(/T([0-9]{2}:[0-9]{2})(?::[0-9]{2})?(?:\.[0-9]{3})?(?:Z|[+-][0-9:]+)$/, 'T$1');
    }

    if (inputType.value === 'number' && props.autoRound) {
      const numValue = Number(value.value);

      if (isNaN(numValue)) {
        return value.value;
      }

      let precision = 0;
      if (props.step) {
        const step = String(props.step);
        const dotPos = step.lastIndexOf('.');
        precision = step.substring(dotPos < 0 ? step.length : dotPos + 1).length;
      }

      precision = Math.pow(10, precision);
      return Math.round((numValue + Number.EPSILON) * precision) / precision;
    }

    return value.value;
  });

  const typePattern = computed(() => {
    const type = props.type ?? 'text';
    return customTypes[type]?.pattern;
  });

  const typeTitle = computed(() => {
    const type = props.type ?? 'text';
    return customTypes[type]?.title;
  });

  onBeforeUpdate(() => {
    withSlot.prepend = Boolean(instance?.$slots.prepend);
    withSlot.append = Boolean(instance?.$slots.append);
  });

  function update(v: unknown) {
    // this.isInvalid = false;
    value.value = v as any;
  }

  function reset() {
    if (typeof props.resetValue !== 'undefined' && value.value !== props.resetValue) {
      update(props.resetValue);
    }
  }

  function doValidate() {
    if (effectiveDisabled.value || !value.value) {
      setCustomValidity(effectiveDisabled.value ? '' : (props.customValidity ?? ''));
      return;
    }

    let testFn: (value: unknown) => boolean | string = () => '';
    const type = props.type ?? 'text';
    const customTypeTest = customTypes[type]?.test;
    if (customTypeTest) {
      testFn = value => customTypeTest(value);
    }

    const result = testFn(value.value);
    if (typeof result === 'string') {
      setCustomValidity(result || (props.customValidity ?? ''));
    } else if (result === false) {
      setCustomValidity(typeTitle.value ?? $t('Formato non valido'));
    } else {
      setCustomValidity(props.customValidity ?? '');
    }
  }

  function change(e: Event) {
    if (!(e.target instanceof HTMLInputElement)) {
      return;
    }

    if (inputType.value === 'datetime-local') {
      const date = parseDateTime(e.target.value);

      if ((value.value as any) instanceof DateTime) {
        update(date);
        return;
      }

      if (typeof value.value === 'string') {
        update(date.toISO());
        return;
      }

      if (typeof value.value === 'number') {
        update(date.toMillis() / (value.value > 10000000000 ? 1 : 1000));
        return;
      }
    }

    update(e.target.value);
  }

  const { refreshValidity, setCustomValidity, ...inputValidityRest } = useInputValidity(input, doValidate);

  watch([effectiveDisabled, () => [props.type, props.customValidity]], refreshValidity);

  return {
    /** Handler per l'evento change dell'input (gestisce conversioni tipo per datetime, number, ecc.) */
    change,
    /** Esegue la validazione custom in base al tipo di input configurato */
    doValidate,
    /** Computed reattivo per lo stato checked (per input con checkbox) */
    effectiveChecked,
    /** Computed che determina se l'input è disabilitato (props.disabled + groupDisabled + withCheckbox logic) */
    effectiveDisabled,
    /** Stato disabled iniettato dal form group padre */
    groupDisabled,
    /** Computed che indica se l'input ha addon dopo il campo (append slot o prop) */
    hasPostAddon,
    /** Computed che indica se l'input ha addon prima del campo (prepend slot/prop o checkbox) */
    hasPreAddon,
    /** Ref all'elemento input DOM o componente PatternFly */
    input,
    /** Computed che restituisce il tipo HTML nativo dell'input (gestisce alias come 'new-password' -> 'password') */
    inputType,
    /** Computed con il valore formattato per l'input (gestisce datetime formatting e autoRound per numeri) */
    inputValue,
    /** Resetta lo stato di validità e pianifica un nuovo check al prossimo tick */
    refreshValidity,
    /** Resetta l'input al valore di resetValue se diverso dal valore corrente */
    reset,
    /** Computed con il pattern regex per il tipo di input custom (se definito) */
    typePattern,
    /** Computed con il messaggio di errore predefinito per il tipo di input custom (se definito) */
    typeTitle,
    /** Aggiorna il valore del v-model */
    update,
    /** Ref reattivo al valore corrente del v-model (gestito con useManagedProp) */
    value,
    ...inputValidityRest,
  };
}
